import { StandardEvents } from '@shopify/events';
const root = document.getElementById('kc-upsell');
if (root && !root.dataset.initialized) {
  root.dataset.initialized = 'true';

  // AB test v1: stable browser assignment on FIRST successful magnet add (not on mere product views).
  // Variant A shows the mounting-plate popup; variant B goes straight to the basket.
  // Shopify Customer Events receive anonymous experiment events. Cart attributes pass the variant to orders.
  const kcAB = (() => {
    const experiment = 'kc_popup_friction_v1';
    const storageKey = 'kopchant_ab_popup_friction_v1';
    let fallbackGroup = null;
    function group() {
      // Preview-only reproducible QA: append ?preview_theme_id=...&kc_ab=A or kc_ab=B.
      // No visitor can force a production cohort using kc_ab alone.
      const params = new URLSearchParams(window.location.search);
      const inPreview = params.has('preview_theme_id') || Boolean(window.Shopify?.designMode);
      const qaGroup = inPreview ? params.get('kc_ab') : null;
      if (qaGroup === 'A' || qaGroup === 'B') return qaGroup;
      if (fallbackGroup) return fallbackGroup;
      let stored = null;
      try { stored = window.localStorage.getItem(storageKey); } catch (_) {}
      if (stored !== 'A' && stored !== 'B') {
        const random = new Uint32Array(1);
        if (window.crypto?.getRandomValues) window.crypto.getRandomValues(random);
        else random[0] = Math.floor(Math.random() * 4294967296);
        stored = random[0] % 2 === 0 ? 'A' : 'B';
        try { window.localStorage.setItem(storageKey, stored); } catch (_) {}
      }
      fallbackGroup = stored;
      return stored;
    }
    function track(event, extra = {}) {
      const payload = {
        experiment,
        group: group(),
        product_id: root.dataset.productId,
        ...extra
      };
      // A configured Shopify custom/app pixel can subscribe without exposing tracking credentials in the theme.
      try { window.Shopify?.analytics?.publish?.(event, payload); }
      catch (error) { console.warn('[KopChant AB] Customer event failed', error); }
      document.dispatchEvent(new CustomEvent('kc:ab:event', { detail: { event, ...payload } }));
    }
    async function stampCart() {
      const path = (root.dataset.rootPath || '/').replace(/\/?$/, '/');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      try {
        const response = await fetch(path + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { KC_AB_Test: experiment, KC_AB_Group: group() } }),
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart attributes HTTP ' + response.status);
      } finally {
        clearTimeout(timeout);
      }
    }
    async function eligible(quantity) {
      const variant = group();
      track('kc_ab_eligible', { quantity });
      try { await stampCart(); }
      catch (error) {
        // Never block the buyer if attribution fails. A pixel event still exists for consented analytics.
        console.warn('[KopChant AB] Could not save order attribution:', error);
        track('kc_ab_cart_attribution_failed');
      }
      return variant;
    }
    return { group, track, eligible };
  })();

  const modal = root.querySelector('dialog');
  const photo = root.querySelector('[data-kc-selected-magnet]');
  const errorNode = root.querySelector('[data-kc-error]');
  const accept = root.querySelector('[data-kc-accept]');
  const decrease = root.querySelector('[data-kc-qty-minus]');
  const increase = root.querySelector('[data-kc-qty-plus]');
  const quantityNode = root.querySelector('[data-kc-quantity]');
  const priceQuantity = root.querySelector('[data-kc-price-quantity]');
  const locale = root.dataset.locale;
  const language = {
    en: {added:'ADDED TO CART',heading:'Before you go to your basket...',intro:'Complete your purchase with this magnetic mounting plate.',offer:'EXCLUSIVE ADD-ON',name:'Add a mounting plate',note:'For suitable non-magnetic surfaces. Sound magnet sold separately.',cta:'ADD THE PLATE FOR {price}',decline:'CONTINUE TO BASKET',error:'Could not add the mounting plate. Please try again.'},
    fr: {added:'AJOUTÉ AU PANIER',heading:'Avant de passer au panier...',intro:'Complète ton achat avec cette plaque métallique adhésive.',offer:'OFFRE EXCLUSIVE',name:'Ajoute la plaque métallique',note:'Pour les surfaces non magnétiques adaptées. Magnet sonore vendu séparément.',cta:'AJOUTER LA PLAQUE À {price}',decline:'CONTINUER VERS LE PANIER',error:'Impossible d’ajouter la plaque. Réessaie.'},
    es: {added:'AÑADIDO AL CARRITO',heading:'Antes de ir al carrito...',intro:'Completa tu compra con esta placa metálica adhesiva.',offer:'OFERTA EXCLUSIVA',name:'Añade la placa metálica',note:'Para superficies no magnéticas adecuadas. Imán sonoro vendido por separado.',cta:'AÑADIR LA PLACA POR {price}',decline:'CONTINUAR AL CARRITO',error:'No se pudo añadir la placa. Inténtalo de nuevo.'},
    it: {added:'AGGIUNTO AL CARRELLO',heading:'Prima di andare al carrello...',intro:'Completa il tuo acquisto con questa piastra metallica adesiva.',offer:'OFFERTA ESCLUSIVA',name:'Aggiungi la piastra metallica',note:'Per superfici non magnetiche adatte. Magnete sonoro venduto separatamente.',cta:'AGGIUNGI LA PIASTRA A {price}',decline:'CONTINUA AL CARRELLO',error:'Impossibile aggiungere la piastra. Riprova.'},
    de: {added:'ZUM WARENKORB HINZUGEFÜGT',heading:'Bevor du zum Warenkorb gehst...',intro:'Ergänze deine Bestellung mit dieser selbstklebenden Metallplatte.',offer:'EXKLUSIVES ANGEBOT',name:'Metallplatte hinzufügen',note:'Für geeignete nichtmagnetische Oberflächen. Soundmagnet separat erhältlich.',cta:'METALLPLATTE FÜR {price} HINZUFÜGEN',decline:'WEITER ZUM WARENKORB',error:'Die Metallplatte konnte nicht hinzugefügt werden. Bitte erneut versuchen.'}
  }[locale] || {
    added:'ADDED TO CART',heading:'Before you go to your basket...',intro:'Complete your purchase with this magnetic mounting plate.',offer:'EXCLUSIVE ADD-ON',name:'Add a mounting plate',note:'For suitable non-magnetic surfaces. Sound magnet sold separately.',cta:'ADD THE PLATE FOR {price}',decline:'CONTINUE TO BASKET',error:'Could not add the mounting plate. Please try again.'
  };
  for (const item of root.querySelectorAll('[data-kc-translation]')) {
    const key = item.dataset.kcTranslation;
    item.textContent = (language[key] || '').replace('{price}',root.dataset.upsellPrice);
  }
  // The quantity reflects the number of sound magnets just added, not a calculated total.
  const quantitiesLabel = {
    en:'Mounting plates', fr:'Plaques métalliques', es:'Placas metálicas',
    it:'Piastre metalliche', de:'Metallplatten'
  };
  const multipleCta = {
    en:'ADD {quantity} PLATES FOR {price} × {quantity}',
    fr:'AJOUTER {quantity} PLAQUES À {price} × {quantity}',
    es:'AÑADIR {quantity} PLACAS POR {price} × {quantity}',
    it:'AGGIUNGI {quantity} PIASTRE A {price} × {quantity}',
    de:'{quantity} METALLPLATTEN FÜR {price} × {quantity} HINZUFÜGEN'
  };
  root.querySelector('[data-kc-quantity-label]').textContent = quantitiesLabel[locale] || quantitiesLabel.en;
  // Customers can choose more plates than magnets; the added magnet count is only a suggestion.
  const maxPlateQuantity = 99;
  let plateQuantity = 1;
  function renderQuantity() {
    quantityNode.textContent = String(plateQuantity);
    priceQuantity.textContent = '× ' + plateQuantity;
    decrease.disabled = plateQuantity <= 1;
    increase.disabled = plateQuantity >= maxPlateQuantity;
    const template = plateQuantity === 1 ? language.cta : (multipleCta[locale] || multipleCta.en);
    accept.querySelector('[data-kc-translation="cta"]').textContent = template
      .replaceAll('{price}',root.dataset.upsellPrice)
      .replaceAll('{quantity}',String(plateQuantity));
  }
  function setPlateQuantity(value) {
    plateQuantity = Math.min(maxPlateQuantity, Math.max(1, Math.floor(Number(value) || 1)));
    renderQuantity();
  }
  decrease.addEventListener('click',() => setPlateQuantity(plateQuantity - 1));
  increase.addEventListener('click',() => setPlateQuantity(plateQuantity + 1));
  renderQuantity();
  let variants = [];
  try { variants = JSON.parse(root.querySelector('#kc-upsell-product-variants').textContent); } catch (_) {}
  const getMainForm = () => [...document.querySelectorAll('product-form-component')].find(form =>
    form.dataset.productId === root.dataset.productId &&
    !form.closest('.same-product-bundle') && !form.closest('.quick-add-modal')
  );
  function syncProductChoice() {
    const mainForm = getMainForm();
    const currentId = mainForm?.querySelector('input[name="id"]')?.value;
    if (!currentId) return;
    for (const bundleForm of document.querySelectorAll('.same-product-bundle product-form-component form')) {
      const field = bundleForm.querySelector('input[name="id"]');
      if (field && field.value !== currentId) field.value = currentId;
    }
    const variant = variants.find(v => String(v.id) === String(currentId));
    if (photo) photo.src = variant?.featured_image?.src || root.dataset.mainImage || photo.src;
  }
  // Synchronise the bundle with the customer's currently selected sound variant.
  document.addEventListener('click', e => { if(e.target.closest('.same-product-bundle__button')) syncProductChoice(); }, true);
  document.addEventListener(StandardEvents.productSelect, () => queueMicrotask(syncProductChoice));
  syncProductChoice();

  const drawerComponent = document.querySelector('cart-drawer-component');
  // Let Horizon update cart counts, but prevent its drawer auto-opening before our native modal.
  drawerComponent?.removeAttribute('auto-open');
  function cartPage() { window.location.assign(root.dataset.cartPath); }
  function showPopup() {
    if (modal.open) return;
    syncProductChoice();
    drawerComponent?.closest('theme-drawer')?.close?.();
    requestAnimationFrame(() => { if (!modal.open) { modal.showModal(); kcAB.track('kc_ab_popup_viewed'); } });
  }
  // Horizon emits this promise-backed event for both single item and existing bundle form adds.
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    if (event.action !== 'add' || event.context !== 'product' || !event.promise) return;
    const source = event.target?.closest?.('product-form-component');
    if (!source || source.dataset.productId !== root.dataset.productId) return;
    const eventQuantity = (event.lines || []).reduce((sum, line) => sum + Math.max(0, Number(line.quantity) || 0), 0);
    event.promise.then(async ({detail}) => {
      if (detail?.didError) return;
      const suggestedPlateQuantity = Math.max(1, Math.floor(Number(detail?.itemCount) || eventQuantity || 1));
      const group = await kcAB.eligible(suggestedPlateQuantity);
      if (group === 'B') {
        // Control path: the original successful add is already in the basket. No popup, no second add.
        cartPage();
        return;
      }
      setPlateQuantity(suggestedPlateQuantity);
      showPopup();
    }).catch(error => console.warn('[KopChant AB] Magnet add failed',error));
  });
  function declinePopup() { kcAB.track('kc_ab_popup_declined'); cartPage(); }
  root.querySelector('[data-kc-decline]').addEventListener('click',declinePopup);
  root.querySelector('[data-kc-close]').addEventListener('click',declinePopup);
  modal.addEventListener('cancel',event => {event.preventDefault();declinePopup();});
  modal.addEventListener('click',event => {
    const area = modal.getBoundingClientRect();
    if(event.clientX < area.left || event.clientX > area.right || event.clientY < area.top || event.clientY > area.bottom) declinePopup();
  });
  accept.addEventListener('click',async () => {
    if (accept.disabled) return;
    accept.disabled = true;
    errorNode.hidden = true;
    const path = root.dataset.rootPath.replace(/\/?$/,'/');
    try {
      const response = await fetch(path + 'cart/add.js',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({id:Number(root.dataset.variantId),quantity:plateQuantity})
      });
      if (!response.ok) throw new Error('HTTP '+response.status);
      kcAB.track('kc_ab_popup_accepted', { plate_quantity: plateQuantity });
      cartPage();
    } catch (error) {
      console.error('[KopChant upsell]',error);
      errorNode.textContent = language.error;
      errorNode.hidden = false;
      accept.disabled = false;
    }
  });
}